import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";

const STATUS_STYLES = {
  draft: "bg-slate-100 text-slate-700 border-slate-300",
  submitted: "bg-blue-50 text-blue-700 border-blue-300",
  in_validation: "bg-amber-50 text-amber-700 border-amber-300",
  validated: "bg-indigo-50 text-indigo-700 border-indigo-300",
  follow_up_open: "bg-rose-50 text-rose-700 border-rose-300",
  open: "bg-rose-50 text-rose-700 border-rose-300",
  responded: "bg-blue-50 text-blue-700 border-blue-300",
  verified: "bg-indigo-50 text-indigo-700 border-indigo-300",
  closed: "bg-emerald-50 text-emerald-700 border-emerald-300",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-300",
  pending: "bg-amber-50 text-amber-700 border-amber-300",
  in_progress: "bg-amber-50 text-amber-700 border-amber-300",
  active: "bg-emerald-50 text-emerald-700 border-emerald-300",
  suspended: "bg-amber-50 text-amber-700 border-amber-300",
  revoked: "bg-rose-50 text-rose-700 border-rose-300",
};

const LABELS = {
  in_validation: "In Validation",
  follow_up_open: "Follow-up Open",
  in_progress: "In Progress",
};

export function StatusBadge({ status, className }) {
  const key = (status || "").toLowerCase();
  const label = LABELS[key] || (status || "").replace(/_/g, " ");
  return (
    <span
      data-testid={`status-badge-${key}`}
      className={cn(
        "inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap",
        STATUS_STYLES[key] || "bg-slate-100 text-slate-700 border-slate-300",
        className
      )}
    >
      {label}
    </span>
  );
}

export function PageHeader({ overline, title, description, actions }) {
  return (
    <div className="flex flex-col gap-3 border-b border-border pb-5 mb-6 sm:flex-row sm:items-end sm:justify-between">
      <div>
        {overline && <div className="overline mb-1.5">{overline}</div>}
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-foreground">{title}</h1>
        {description && <p className="mt-1.5 text-sm text-muted-foreground max-w-2xl">{description}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
  );
}

export function StatCard({ label, value, sub, icon: Icon, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className="relative bg-card border border-border rounded-md p-5 transition-colors hover:border-slate-400"
    >
      <div className="flex items-start justify-between">
        <div className="overline">{label}</div>
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} aria-hidden="true" />}
      </div>
      <div className={cn("mt-2 font-heading text-3xl font-bold tracking-tight", accent || "text-foreground")}>
        {value}
      </div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

export function Loader({ label = "Loading" }) {
  return (
    <div className="flex items-center justify-center py-20 text-muted-foreground" data-testid="loader">
      <Loader2 className="h-5 w-5 animate-spin mr-2" aria-hidden="true" />
      <span className="text-sm">{label}…</span>
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center border border-dashed border-border rounded-md bg-card/50">
      {Icon && <Icon className="h-8 w-8 text-muted-foreground mb-3" strokeWidth={1.5} aria-hidden="true" />}
      <h3 className="font-heading font-semibold text-foreground">{title}</h3>
      {description && <p className="mt-1 text-sm text-muted-foreground max-w-sm">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-SG", {
      year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function PoweredByOutgrow({ className }) {
  return (
    <a
      href="https://outgrow.co"
      target="_blank"
      rel="noopener noreferrer"
      data-testid="powered-by-outgrow"
      className={cn(
        "inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors",
        className
      )}
    >
      Powered by <span className="font-semibold text-foreground">Outgrow.co</span>
    </a>
  );
}
